import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PureQueryOptions } from '@apollo/client';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentConversationsListQuery,
  useGroupConversationsListQuery,
  usePostSlackReplyMutation,
  usePostGmailReplyMutation,
  useMarkConvReadMutation,
  useSetConvStatusMutation,
  StudentConversationsListDocument,
  GroupConversationsListDocument,
  ConversationMessagesListDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

export type ConversationStatus = 'open' | 'in_progress' | 'done';

export interface IConversation {
  id: string;
  kind: string;
  title: string;
  /** Human dialog title (channel name / mail subject / counterpart name). */
  displayTitle: string;
  externalId: string;
  /** Slack workspace team id — powers the «open in Slack» deep link. */
  teamId: string;
  mailboxEmail: string | null;
  unreadCount: number;
  status: string;
  isUnanswered: boolean;
  lastMessageAt: string | null;
}

interface IArgs {
  studentId?: string;
  groupId?: string;
  /**
   * Extra refetches appended to every triage mutation (reply / markRead /
   * setStatus) — the communicator hands its badge queries here so the
   * column counters update immediately after an action.
   */
  extraRefetch?: PureQueryOptions[];
}

interface IUseConversations {
  conversations: IConversation[];
  loading: boolean;
  replying: boolean;
  reply: (
    conversationId: string,
    text: string,
    kind: string,
  ) => Promise<boolean>;
  /** Mark every message of a conversation read/unread (team-wide) + refetch. */
  markRead: (conversationId: string, read?: boolean) => Promise<void>;
  /** Set the handling status (open / in_progress / done) + refetch. */
  setStatus: (
    conversationId: string,
    status: ConversationStatus,
  ) => Promise<void>;
  /**
   * Refetch options for the conversation list — hand these to a sibling
   * mutation (e.g. create-task-from-message) so the list refreshes too.
   */
  listRefetch: PureQueryOptions | null;
}

const GMAIL_KIND = 'gmail_thread';

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Slack conversations for a student OR a group + reply (spec 0003 Phase 3).
 * A reply refetches both the conversation list (answered flag flips) and the
 * open thread.
 */
export const useConversations = ({
  studentId,
  groupId,
  extraRefetch,
}: IArgs): IUseConversations => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  // errorPolicy:'all' so a PermissionDenied (e.g. a non-management role opening
  // a card) degrades to an empty panel instead of an unhandled query error.
  const studentQuery = useStudentConversationsListQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const groupQuery = useGroupConversationsListQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const listRefetch = useMemo(() => {
    if (studentId) {
      return {
        query: StudentConversationsListDocument,
        variables: { studentId },
      };
    }
    if (groupId) {
      return { query: GroupConversationsListDocument, variables: { groupId } };
    }
    return null;
  }, [studentId, groupId]);

  const [slackMut, slackState] = usePostSlackReplyMutation();
  const [gmailMut, gmailState] = usePostGmailReplyMutation();
  const [markConvRead] = useMarkConvReadMutation();
  const [setConvStatus] = useSetConvStatusMutation();

  const baseRefetch = useMemo(
    () => [...(listRefetch ? [listRefetch] : []), ...(extraRefetch || [])],
    [listRefetch, extraRefetch],
  );

  const markRead = useCallback(
    async (conversationId: string, read = true) => {
      try {
        await markConvRead({
          variables: { conversationId, read },
          awaitRefetchQueries: true,
          refetchQueries: baseRefetch,
        });
      } catch {
        // Non-critical: a failed read-marker shouldn't disrupt reading.
      }
    },
    [markConvRead, baseRefetch],
  );

  const setStatus = useCallback(
    async (conversationId: string, status: ConversationStatus) => {
      try {
        await setConvStatus({
          variables: { conversationId, status },
          awaitRefetchQueries: true,
          refetchQueries: baseRefetch,
        });
      } catch {
        // Non-critical.
      }
    },
    [setConvStatus, baseRefetch],
  );

  const reply = useCallback(
    async (conversationId: string, text: string, kind: string) => {
      const isGmail = kind === GMAIL_KIND;
      const opts = {
        variables: { conversationId, text },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: ConversationMessagesListDocument,
            variables: { conversationId },
          },
          ...baseRefetch,
        ],
      };
      try {
        const p = isGmail
          ? (await gmailMut(opts)).data?.sendGmailReply
          : (await slackMut(opts)).data?.sendSlackMessage;
        if (p?.success) return true;
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.failed'),
            text: errText(p?.customErrors),
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return false;
      } catch (e) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.failed'),
            text: (e as Error).message,
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return false;
      }
    },
    [slackMut, gmailMut, baseRefetch, addToast, t],
  );

  const rawConversations = (studentId
    ? studentQuery.data?.studentConversations
    : groupQuery.data?.groupConversations) as IConversation[] | undefined;

  // Backend resolves displayTitle for every kind; the || chain only guards
  // against a stale cache entry fetched before the field existed.
  const conversations = useMemo(
    () =>
      (rawConversations || []).map((c) => ({
        ...c,
        displayTitle: c.displayTitle || c.title || c.externalId,
        teamId: c.teamId || '',
      })),
    [rawConversations],
  );

  return {
    conversations,
    loading: studentId ? studentQuery.loading : groupQuery.loading,
    replying: slackState.loading || gmailState.loading,
    reply,
    markRead,
    setStatus,
    listRefetch,
  };
};
